import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  Checkbox,
  FormControlLabel,
  Button,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Styled components
const LegalContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  maxHeight: 300,
  overflow: 'auto'
}));

const LegalSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`terms-tabpanel-${index}`}
      aria-labelledby={`terms-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const TermsAndPrivacy = ({ onAccept, onDecline }) => {
  const [tabValue, setTabValue] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCheckboxChange = (event) => {
    setAccepted(event.target.checked);
  };

  const openDialog = (title, content) => {
    setDialogTitle(title);
    setDialogContent(content);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Terms and Privacy
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review and accept our terms of service and privacy policy before continuing
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Terms of Service" />
          <Tab label="Privacy Policy" />
          <Tab label="Data Usage" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <LegalContainer>
            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                1. Acceptance of Terms
              </Typography>
              <Typography variant="body2">
                By accessing or using TeamScout, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this platform.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                2. Use License
              </Typography>
              <Typography variant="body2">
                Permission is granted to temporarily use TeamScout for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                <ul>
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose</li>
                  <li>Attempt to decompile or reverse engineer any software contained in TeamScout</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                  <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                </ul>
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                3. Disclaimer
              </Typography>
              <Typography variant="body2">
                The materials on TeamScout are provided on an 'as is' basis. TeamScout makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                4. Limitations
              </Typography>
              <Typography variant="body2">
                In no event shall TeamScout or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use TeamScout, even if TeamScout or a TeamScout authorized representative has been notified orally or in writing of the possibility of such damage.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                5. Revisions and Errata
              </Typography>
              <Typography variant="body2">
                The materials appearing on TeamScout could include technical, typographical, or photographic errors. TeamScout does not warrant that any of the materials on its platform are accurate, complete or current. TeamScout may make changes to the materials contained on its platform at any time without notice.
              </Typography>
            </LegalSection>
          </LegalContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <LegalContainer>
            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                1. Information Collection
              </Typography>
              <Typography variant="body2">
                We collect several different types of information for various purposes to provide and improve our service to you. Types of Data Collected include:
                <ul>
                  <li>Personal Data: Email address, first name, last name, phone number, address</li>
                  <li>Usage Data: Information on how you access and use our service</li>
                  <li>Tracking & Cookies Data: We use cookies and similar tracking technologies</li>
                </ul>
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                2. Use of Data
              </Typography>
              <Typography variant="body2">
                TeamScout uses the collected data for various purposes:
                <ul>
                  <li>To provide and maintain our service</li>
                  <li>To notify you about changes to our service</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information so that we can improve our service</li>
                  <li>To monitor the usage of our service</li>
                  <li>To detect, prevent and address technical issues</li>
                </ul>
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                3. Transfer of Data
              </Typography>
              <Typography variant="body2">
                Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                4. Disclosure of Data
              </Typography>
              <Typography variant="body2">
                TeamScout may disclose your Personal Data in the good faith belief that such action is necessary to:
                <ul>
                  <li>To comply with a legal obligation</li>
                  <li>To protect and defend the rights or property of TeamScout</li>
                  <li>To prevent or investigate possible wrongdoing in connection with the service</li>
                  <li>To protect the personal safety of users of the service or the public</li>
                  <li>To protect against legal liability</li>
                </ul>
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                5. Security of Data
              </Typography>
              <Typography variant="body2">
                The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
              </Typography>
            </LegalSection>
          </LegalContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <LegalContainer>
            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                1. Data Storage
              </Typography>
              <Typography variant="body2">
                TeamScout stores your data on secure servers and employs industry-standard encryption technologies to protect your information. We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                2. Data Sharing
              </Typography>
              <Typography variant="body2">
                We do not sell, trade, or rent your personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners and trusted affiliates.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                3. Third-Party Services
              </Typography>
              <Typography variant="body2">
                TeamScout may use third-party service providers to help us operate our business and the platform or administer activities on our behalf. We may share your information with these third parties for those limited purposes provided that you have given us your permission.
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                4. User Rights
              </Typography>
              <Typography variant="body2">
                You have the right to:
                <ul>
                  <li>Access your personal data</li>
                  <li>Correct inaccurate personal data</li>
                  <li>Request erasure of your personal data</li>
                  <li>Object to processing of your personal data</li>
                  <li>Request restriction of processing your personal data</li>
                  <li>Request transfer of your personal data</li>
                  <li>Withdraw consent</li>
                </ul>
              </Typography>
            </LegalSection>

            <LegalSection>
              <Typography variant="subtitle1" gutterBottom>
                5. Data Retention
              </Typography>
              <Typography variant="body2">
                We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.
              </Typography>
            </LegalSection>
          </LegalContainer>
        </TabPanel>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={handleCheckboxChange}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to the{' '}
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => openDialog('Terms of Service', 'terms')}
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => openDialog('Privacy Policy', 'privacy')}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        <IconButton 
          size="small" 
          color="primary"
          onClick={() => openDialog('Legal Information', 'info')}
          sx={{ ml: 1 }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onDecline}
        >
          Decline
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!accepted}
          onClick={onAccept}
        >
          Accept & Continue
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogTitle}
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogContent === 'terms' && (
            <Box>
              <Typography variant="h6" gutterBottom>Terms of Service</Typography>
              <Typography variant="body2" paragraph>
                These Terms of Service govern your use of the TeamScout platform and constitute a binding legal agreement between you and TeamScout.
              </Typography>
              
              {/* Full terms content would go here */}
              <Typography variant="body2" paragraph>
                By using TeamScout, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the platform.
              </Typography>
            </Box>
          )}
          
          {dialogContent === 'privacy' && (
            <Box>
              <Typography variant="h6" gutterBottom>Privacy Policy</Typography>
              <Typography variant="body2" paragraph>
                This Privacy Policy describes how your personal information is collected, used, and shared when you use TeamScout.
              </Typography>
              
              {/* Full privacy policy content would go here */}
              <Typography variant="body2" paragraph>
                We take your privacy seriously and are committed to protecting your personal information. This Privacy Policy is designed to help you understand what information we collect, how we use it, and what choices you have.
              </Typography>
            </Box>
          )}
          
          {dialogContent === 'info' && (
            <Box>
              <Typography variant="h6" gutterBottom>Legal Information</Typography>
              <Typography variant="body2" paragraph>
                TeamScout is committed to protecting your privacy and ensuring you have a positive experience on our platform. This legal information provides additional context about our terms and policies.
              </Typography>
              
              <Typography variant="body2" paragraph>
                If you have any questions about our Terms of Service or Privacy Policy, please contact our support team at support@teamscout.com.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TermsAndPrivacy;
